<template>
  <label
    class="toggle-button"
    :class="{ disabled, 'is-on': toggled }"
    tabindex="0"
    role="checkbox"
    @keydown.space.prevent="keyToggle"
  >
    <input
      type="checkbox"
      class="toggle-button-input"
      :name="name"
      :checked="value"
      :disabled="disabled"
      tabindex="-1"
      @change.stop="toggle"
    />
    <span class="toggle-button-core">
      <span class="toggle-button-button" />
    </span>
    <template v-if="labels">
      <span v-if="toggled" class="toggle-button-label toggle-button-label-left">
        <span class="toggle-button-label-inner">
          <slot name="on">{{ labelChecked }}</slot>
        </span>
      </span>
      <span v-else class="toggle-button-label toggle-button-label-right">
        <span class="toggle-button-label-inner">
          <slot name="off">{{ labelUnchecked }}</slot>
        </span>
      </span>
    </template>
  </label>
</template>

<script>
export default {
  name: 'ToggleButton',
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    labels: {
      type: [Boolean, Object],
      default: false,
      validator(value) {
        return typeof value === 'object'
          ? value.on || value.off
          : typeof value === 'boolean'
      },
    },
  },
  computed: {
    labelChecked() {
      return this.labels.on || this.$t('components.toggle-button.on')
    },
    labelUnchecked() {
      return this.labels.off || this.$t('components.toggle-button.off')
    },
  },
  data() {
    return {
      toggled: !!this.value,
    }
  },
  watch: {
    value(value) {
      this.toggled = !!value
    },
  },
  methods: {
    keyToggle(event) {
      if (this.disabled) {
        return
      }

      this.toggle(event)
    },
    toggle(event) {
      this.toggled = !this.toggled

      this.$emit('input', this.toggled)
      this.$emit('change', {
        value: this.toggled,
        srcEvent: event,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.toggle-button {
  --height: 1.375em;
  --width: 3em;
  --inner-margin: 0.3em;
  --inner-button-radius: calc(var(--height) - var(--inner-margin) * 2);
  --inner-button-x-position: var(--inner-margin);
  --label-font-size: 0.625em;

  display: inline-block;
  position: relative;
  vertical-align: middle;
  user-select: none;
  height: var(--height);
  width: var(--width);
  margin: 0.3em;
  cursor: pointer;
  background-color: var(--gray);
  border-radius: calc(var(--height) / 2);

  &.is-on {
    --inner-button-x-position: calc(
      var(--width) - var(--height) + var(--inner-margin)
    );
    background-color: $green;
  }

  .toggle-button-input {
    opacity: 0;
    position: absolute;
    width: 1px;
    height: 1px;
  }

  .toggle-button-label {
    position: absolute;
    display: block;
    top: 0;
    font-weight: 600;
    color: white;
    z-index: 1;
    line-height: calc(var(--height) - 0.1em);
  }

  .toggle-button-label-inner {
    font-size: var(--label-font-size);
  }

  .toggle-button-label-left {
    left: 0.5em;
  }
  .toggle-button-label-right {
    right: 0.5em;
  }

  .toggle-button-core {
    display: block;
    position: relative;
    box-sizing: border-box;
    outline: 0;
    margin: 0;
    transition: border-color var(--transition-medium),
      background-color var(--transition-medium);
    user-select: none;

    .toggle-button-button {
      display: block;
      position: absolute;
      overflow: hidden;
      top: 0;
      left: 0;
      height: var(--inner-button-radius);
      width: var(--inner-button-radius);
      border-radius: 100%;
      background-color: $white;
      z-index: 2;
      transform: translate(var(--inner-button-x-position), var(--inner-margin));
      transition: transform var(--transition-medium);
    }
  }

  &.disabled {
    pointer-events: none;
    opacity: 0.6;
  }
}
</style>
